import React, { useState, useEffect, useCallback } from "react";
import { getUsers, deleteUser } from "./api/users";
import { getRoles } from "../roles/api/roles";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { UsersDataTable } from "./components/users-data-table";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import { Role, User } from "types/settings";

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, rolesResponse] = await Promise.all([
          getUsers(),
          getRoles()
        ]);
        setUsers(usersResponse || []);
        setRoles(rolesResponse || []);
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err?.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchUsersData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getUsers();
      setUsers(response || []);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setError(err?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteUser = async (user_id: string) => {
    try {
      await deleteUser(user_id);
      await fetchUsersData();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };

  const handleEditUser = (user: User) => {
    setUserToEdit(user);
    setEditModalOpen(true);
  };

  const handleUserUpdated = async () => {
    await fetchUsersData();
  };

  const handleUserCreated = async () => {
    await fetchUsersData();
  };


  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="h-[600px]">
        <UsersDataTable
          data={users}
          onDeleteUser={handleDeleteUser}
          onEditUser={handleEditUser}
          onRefreshData={fetchUsersData}
          onCreateUser={() => setCreateModalOpen(true)}
        />
      </div>

      <CreateUserModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onUserCreated={handleUserCreated}
        roles={roles}
      />

      <EditUserModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        user={userToEdit}
        onUserUpdated={handleUserUpdated}
        roles={roles}
      />
    </>
  );
};

export default Users;
